/* eslint-disable dot-notation */
import EventEmitter from "eventemitter3";
import {
  Events,
  PageViewEvent,
  SellFormViewEvent,
  SYBSaveDraftEvent,
  SellFormCompletedEvent,
  ModalEvent,
  TrackingEvent,
  EventUserId,
  ShiplistSubmit,
  VAFormCompletedEvent,
  StepCompleted,
} from "../@types/events";
import { segmentPageNameMap } from "../helpers/segmentPageNameMap";
import Avo from "../Avo";
import { avoFormatter } from "../helpers/avoFormatter";
import { segmentWhitelistFilter } from "./segment/segment";

// NB events.once will disable themselves (after firing) until page reload

// General
const events: Events = new EventEmitter<keyof TrackingEvent>();

events.on(
  ...segmentWhitelistFilter(
    "page_view",
    async ({ page_name, userId }: PageViewEvent) => {
      const mappedName = segmentPageNameMap[page_name];
      const formattedName = mappedName?.name || page_name;
      const site_section = mappedName?.site_section;

      const params = {
        name: formattedName,
        language_code: "en",
        site_section,
        user_id: userId,
        has_recommendation: 0,
      };

      const avoFormatted = avoFormatter(params);
      Avo.pageViewed(avoFormatted);
    }
  )
);

events.on(
  ...segmentWhitelistFilter("modal_open", async (data: ModalEvent) => {
    const avoFormatted = avoFormatter(data);
    Avo.modalOpened(avoFormatted);
  })
);

events.on(
  ...segmentWhitelistFilter("modal_close", async (data: ModalEvent) => {
    const avoFormatted = avoFormatter(data);
    Avo.modalClosed(avoFormatted);
  })
);

// SYB
events.on(
  ...segmentWhitelistFilter(
    "sell_form_view",
    async (data: SellFormViewEvent) => {
      const params = {
        sell_form_type: "Web Quote",
        step_name: "Form Viewed",
        user_id: +data.customer_id,
        step: 1,
      };
      const avoFormatted = avoFormatter(params);
      Avo.quoteFormStarted(avoFormatted);
    }
  )
);

const webQuoteStepCompleted = async <T extends StepCompleted>(
  data: T
): Promise<void> => {
  const params = {
    sell_form_type: "Web Quote",
    ...data,
  };
  const avoFormatted = avoFormatter(params);
  Avo.quoteFormStepCompleted(avoFormatted);
};

events.on(
  ...segmentWhitelistFilter("syb_designer_selected", webQuoteStepCompleted)
);

events.on(
  ...segmentWhitelistFilter("syb_category_selected", webQuoteStepCompleted)
);

events.on(
  ...segmentWhitelistFilter("web_quote_image_upload", webQuoteStepCompleted)
);

events.on("syb_form_completed", async (data: SellFormCompletedEvent) => {
  window["optimizely"] = window["optimizely"] || [];
  window["optimizely"].push({
    type: "event",
    eventName: "syb-submit-item-success",
    tags: {
      revenue: 0, // Optional in cents as integer (500 == $5.00)
      value: 0.0, // Optional as float
    },
  });
});

events.on(
  ...segmentWhitelistFilter(
    "syb_form_completed",
    async (data: SellFormCompletedEvent) => {
      const segmentData = { ...data };
      const avoFormatted = avoFormatter(segmentData);
      Avo.quoteFormCompleted(avoFormatted);
    }
  )
);

events.on(
  ...segmentWhitelistFilter(
    "syb_save_draft",
    async (data: SYBSaveDraftEvent) => {
      const avoFormatted = avoFormatter(data);
      Avo.quoteFormSaveDraft(avoFormatted);
    }
  )
);

// virtual appt page
events.on(
  ...segmentWhitelistFilter("sell_form_view_va", async (data: EventUserId) => {
    const params = {
      sell_form_type: "Virtual Appointment",
      step_name: "Form Viewed",
      user_id: data.user_id,
      step: 1,
    };
    const avoFormatted = avoFormatter(params);
    Avo.appointmentFormStarted(avoFormatted);
  })
);

const vaStepCompleted = async <T extends StepCompleted>(
  data: T
): Promise<void> => {
  const avoFormatted = avoFormatter(data);
  Avo.appointmentFormStepCompleted(avoFormatted);
};
events.once(
  ...segmentWhitelistFilter(
    "virtual_appointment_date_selected",
    vaStepCompleted
  )
);
events.once(
  ...segmentWhitelistFilter(
    "virtual_appointment_time_selected",
    vaStepCompleted
  )
);
events.on(
  ...segmentWhitelistFilter(
    "virtual_appointment_submitted",
    async (data: VAFormCompletedEvent) => {
      const avoFormatted = avoFormatter(data);
      Avo.appointmentFormCompleted(avoFormatted);
    }
  )
);

// shiplist
events.on("shiplist_submit", async (data: ShiplistSubmit) => {
  window["optimizely"] = window["optimizely"] || [];
  window["optimizely"].push({
    type: "event",
    eventName: "ship_list_submit_identifier",
    tags: data,
  });
});

export default events;
